import type { CtcCommission, CtcCommissionRange } from "../webapp/src/types";

/**
 * Commission ranges of a profile (ctc_commissions.ranges): the calculation (shared so the Back office
 * editor's "Try an amount" gives exactly what the server charges) and the checks the editor shows.
 * Ported from the legacy `KFH.BLL/Services/CommissionService.cs`; see functions-ctc/src/commission.ts.
 *
 * Ranges are written the usual way, each starting at the previous "To" + 1 (0 – 100,000, 100,001 –
 * 1,000,000, …); "end = next start" works too. A range covers amounts from its "From" (included) up to
 * where the next range starts, so an amount exactly on a "From", or with decimals between one range's
 * "To" and the next "From" (100,000.5), is never left without a range. Beyond the last range's "To":
 * no commission. (The legacy rule counted a range only above its "From", which charged 0 on those amounts.)
 */

const floorToMinimum = (value: number, range: CtcCommissionRange) =>
    range.minimumAmount !== undefined && range.minimumAmount !== null && value <= range.minimumAmount ? range.minimumAmount : value;

const isPercentageOnly = (ranges: CtcCommissionRange[]) => ranges.every((r) => !r.isFixedAmount);

const sortedRanges = (ranges?: CtcCommissionRange[]) => [...(ranges ?? [])].sort((a, b) => (a.startAmount ?? 0) - (b.startAmount ?? 0));

/** Index in `sorted` of the range an amount falls in (see the rule above), or -1. */
const bracketIndex = (amount: number, sorted: CtcCommissionRange[]) => {
    if (!(amount > 0)) return -1;
    let i = -1;
    sorted.forEach((r, j) => { if ((r.startAmount ?? 0) <= amount) i = j; });
    if (i < 0) return -1;
    const r = sorted[i];
    const end = r.endAmount;
    const isLast = i === sorted.length - 1;
    // Up to and including its "To"; past it only until the next range starts (the "+1" gap); past the last range: none
    if (end === undefined || end === null || amount <= end || !isLast) return i;
    return -1;
};

/** Commission for an amount already in the profile's currency. */
export const calculateFromRanges = (amountInProfileCurrency: number, profile: Pick<CtcCommission, "isOnRange" | "ranges">): number => {
    const sorted = sortedRanges(profile.ranges);
    const reached = sorted.filter((r) => (r.startAmount ?? 0) <= amountInProfileCurrency);
    if (!(amountInProfileCurrency > 0) || reached.length === 0) return 0;

    if (isPercentageOnly(reached)) {
        if (reached.length === 1) {
            return floorToMinimum(amountInProfileCurrency * ((reached[0].percentage ?? 0) / 100), reached[0]);
        }
        if (profile.isOnRange) {
            const i = bracketIndex(amountInProfileCurrency, sorted);
            return i >= 0 ? floorToMinimum(amountInProfileCurrency * ((sorted[i].percentage ?? 0) / 100), sorted[i]) : 0;
        }
        // cumulative tiers: each tier runs from its "From" to the next tier's "From" (or its own "To" if last)
        let remaining = amountInProfileCurrency - (reached[0].startAmount ?? 0);
        let total = 0;
        for (let k = 0; k < reached.length && remaining > 0; k++) {
            const r = reached[k];
            const next = sorted[sorted.indexOf(r) + 1];
            const width = (next ? next.startAmount ?? 0 : r.endAmount ?? Number.POSITIVE_INFINITY) - (r.startAmount ?? 0);
            const part = Math.min(remaining, width);
            total += floorToMinimum(part * ((r.percentage ?? 0) / 100), r);
            remaining -= part;
        }
        return total;
    }

    const i = bracketIndex(amountInProfileCurrency, sorted);
    if (i < 0) return 0;
    const bracket = sorted[i];
    if (bracket.isFixedAmount) return bracket.fixedAmount ?? 0;
    return floorToMinimum(amountInProfileCurrency * ((bracket.percentage ?? 0) / 100), bracket);
};

/** Which range (0-based, in the given order) an amount falls in, or -1 if none. */
export const ctcRangeIndexFor = (amount: number, ranges: CtcCommissionRange[]) => {
    const sorted = sortedRanges(ranges);
    const i = bracketIndex(amount, sorted);
    return i < 0 ? -1 : ranges.indexOf(sorted[i]);
};

const fmt = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 3 });
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/**
 * Problems with a profile's ranges. `errors` block saving (they'd give wrong or no commission for whole
 * ranges); `warnings` are allowed (existing profiles have them) but shown: amounts in a gap get 0.
 */
export const ctcRangesCheck = (ranges: unknown): { errors: string[]; warnings: string[] } => {
    const errors: string[] = [];
    const warnings: string[] = [];
    if (!Array.isArray(ranges) || ranges.length === 0) return { errors: ["Add at least one range"], warnings };
    const list = ranges as Partial<CtcCommissionRange>[];
    list.forEach((r, i) => {
        const n = i + 1;
        if (!isNum(r.startAmount) || r.startAmount < 0) errors.push(`Range ${n}: "From" must be 0 or more`);
        if (r.endAmount !== undefined && r.endAmount !== null && (!isNum(r.endAmount) || (isNum(r.startAmount) && r.endAmount <= r.startAmount))) {
            errors.push(`Range ${n}: "To" must be above "From" (or empty for no limit)`);
        }
        if (r.isFixedAmount) {
            if (!isNum(r.fixedAmount) || r.fixedAmount < 0) errors.push(`Range ${n}: enter the fixed amount`);
        } else if (!isNum(r.percentage) || r.percentage < 0) {
            errors.push(`Range ${n}: enter the percentage`);
        }
        if (r.minimumAmount !== undefined && r.minimumAmount !== null && (!isNum(r.minimumAmount) || r.minimumAmount < 0)) errors.push(`Range ${n}: minimum must be 0 or more`);
    });
    if (errors.length) return { errors, warnings };

    const sorted = list.map((r, i) => ({ r, n: i + 1 })).sort((a, b) => (a.r.startAmount ?? 0) - (b.r.startAmount ?? 0));
    if ((sorted[0].r.startAmount ?? 0) > 0) warnings.push(`Amounts below ${fmt(sorted[0].r.startAmount ?? 0)} are in no range (commission 0): usually the first range starts at 0`);
    for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const cur = sorted[i];
        const prevEnd = prev.r.endAmount;
        if (prevEnd === undefined || prevEnd === null) {
            errors.push(`Range ${prev.n} has no upper limit, so range ${cur.n} can never apply`);
            continue;
        }
        const start = cur.r.startAmount ?? 0;
        // An amount must be in exactly one range: the next "From" must be above the previous "To"
        if (start <= prevEnd) errors.push(`Ranges ${prev.n} and ${cur.n} overlap: ${fmt(start)}${start === prevEnd ? "" : ` to ${fmt(prevEnd)}`} is in both. Range ${cur.n} must start at ${fmt(prevEnd + 1)}`);
        // "+1" (100,000 → 100,001) or "end = next start" are both fine; a bigger jump is a range nobody meant
        else if (start > prevEnd + 1) warnings.push(`Range ${cur.n} starts at ${fmt(start)}, more than 1 after range ${prev.n} ends (${fmt(prevEnd)}): amounts in between use range ${prev.n}. Usually range ${cur.n} should start at ${fmt(prevEnd + 1)}`);
    }
    return { errors, warnings };
};
